/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardFilters from '../components/DashboardFilters';
import { clampResolution } from '../_helpers/timeResolution';
import {
  TimeSpan,
  ParametersType,
  TimeSpanDateAttributes,
  DashboardDataFilter,
  GraphData,
  summaryAPIProps,
  DataWidgetMinMaxAttribute,
  summaryOverTimeResponse
} from '../constants/telosair-interfaces';
import { DAHBOARD_DEFAULT_FILTER } from '../constants/global-constants';
import {
  getMillSecsByTimeSpan,
  handleTimeSpanChange,
  setupEventSource
} from '../_helpers/dashboardHelper';
import { useUnifiedData } from '../hooks/useUnifiedData';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';
import DashboardSideBar from '../modals/DashboardSideBar';
import { changeZeroValuesToNull } from '../_helpers/globalHelper';
import { cleanGraphData } from '../_helpers/graph';
import {
  getDeviceSummaryOverTime,
  getMinAndMaxOfParameters
} from '../services/unified-dashboard-service';
import Deque, { manageQueue } from '../classes/deque';
import DownloadModal from '../modals/Download';
import InjestDeviceDataModal from '../modals/InjestDeviceData';
import DetailedPage from './dashboard/detailed';
import SimplifiedPage from './dashboard/simplified';
import MapPage from './dashboard/map';
import useStorageState from '../hooks/useStorageState';
import { DashboardView } from '../controls/ViewModeToggle';
import canUseDeviceMonitorViews from '../_helpers/featureAccess';

const DEFAULT_POLLING_TIME = 30000;
/**
 * Home component that represents the main dashboard view.
 * @returns  A React element representing the Home component.
 */
export default function Dashboard(): ReactElement {
  const {
    groupedData,
    loading: devicesLoading,
    fetchSummary,
    devices,
    currentUser,
    dataSummary,
    parameters,
    setDataSummary,
    setGroupDataSummary,
    defaultDashboardFilter
  } = useUnifiedData();

  const [params] = useSearchParams();
  const appliedFilter = (): DashboardDataFilter | null => {
    const type = params.get('type');
    const id = params.get('id');
    const name = params.get('name');
    if (type === 'device' && id) {
      return {
        type: 'device',
        id: Number(id),
        name: name as unknown as string
      };
    }
    if (type === 'group' && id) {
      return { type: 'group', id: Number(id), name: name as unknown as string };
    }
    return getAppliedFilter(Filters.dashboardDataFilter)?.data;
  };

  const [selectedParameter, setSelectedParameter] = useState<ParametersType[] | null>(null);
  const [sidepanelParameter, setSidePanelParameter] = useState<{
    serialNumber: number;
    parameter: ParametersType;
  } | null>(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const timeSpanFilter = getAppliedFilter(Filters.timeSpan)?.data;
  const [timeSpan, setTimeSpan] = useState(timeSpanFilter || TimeSpan.Mins15);
  const [refreshing, setRefreshing] = useState(false);
  const [timeSpanDate, setTimeSpanDate] = useState<TimeSpanDateAttributes | null>(null);
  // Chart bucket width sent as dataPointsDifference. Held unclamped and passed
  // through clampResolution at each point of use, so a width that a longer
  // period disallows is not forgotten if the user returns to a shorter one.
  // Clamping it into state instead would refetch twice on every period change.
  const [resolution, setResolution] = useState<string | null>(null);
  const appliedDataFilter = appliedFilter();
  const [extendedLabel, setExtendedLabel] = useState('');
  const [dataFilter, setDataFilter] = useState<DashboardDataFilter | null>(
    appliedDataFilter || null
  );
  const [hasAppliedDefaultFilter, setHasAppliedDefaultFilter] = useState(false);
  const selectedDevice = dataFilter?.type === 'device' ? dataFilter.id : null;
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphDataRef = useRef<GraphData | null>(null);
  const chartQueueDataRef = useRef<Record<ParametersType, Deque>>({} as any);
  const [, setUpdateTrigger] = useState(false);
  const [minMaxData, setMinMaxData] = useState<DataWidgetMinMaxAttribute | null>(null);
  const timePeriod = getMillSecsByTimeSpan(timeSpan);
  // Persisted dashboard view: 'simple' | 'detailed' | 'map'. Falls back to the
  // legacy "detailed view" boolean for users who had it enabled previously.
  const legacyDetailedView = getAppliedFilter(Filters.dashboardSimplified)?.data === true;
  const [viewMode, setViewMode] = useStorageState<DashboardView>(
    Filters.dashboardView,
    legacyDetailedView ? 'detailed' : 'simple'
  );
  // The redesigned Detailed view is still gated to an allowlist while in
  // limited release; anyone else landing on it (e.g. via the legacy "detailed"
  // default) falls back to Simple. Map is generally available.
  const canUseMonitor = canUseDeviceMonitorViews();
  const availableViews: DashboardView[] = canUseMonitor
    ? ['simple', 'detailed', 'map']
    : ['simple', 'map'];
  const effectiveView: DashboardView = availableViews.includes(viewMode) ? viewMode : 'simple';

  // Use refs for values that don't need to trigger re-fetches
  const groupedDataRef = useRef(groupedData);
  const parametersRef = useRef(parameters);

  useEffect(() => {
    groupedDataRef.current = groupedData;
  }, [groupedData]);

  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);

  useEffect(() => {
    if (timeSpanDate === null) {
      const time =
        timeSpanFilter === TimeSpan.Custom
          ? getAppliedFilter(Filters.timeSpanCustom)?.data
          : handleTimeSpanChange(timeSpan, timeSpanDate);
      setTimeSpanDate({ ...time });
    }
  }, [timeSpan, timeSpanDate]);

  /**
   * Calls handleTimeSpanChange helper and also sets time span.
   * @param span - Time span selected.
   */
  const handleTimeChange = (span: string | TimeSpan): void => {
    setTimeSpan(span as TimeSpan);
    setAppliedFilter({ key: Filters.timeSpan, data: span });
    if (span !== TimeSpan.Custom) {
      setTimeSpanDate({ ...handleTimeSpanChange(span, timeSpanDate) });
    }
    setRefreshing(true);
  };

  const getAllSummaries = useCallback(async () => {
    if (timeSpanDate && (currentUser?.userName || sessionStorage.getItem('isPublicView'))) {
      await fetchSummary(timeSpanDate.startDate, timeSpanDate.endDate);
    }
  }, [timeSpanDate, currentUser]);

  useEffect(() => {
    getAllSummaries();
  }, [getAllSummaries]);

  /**
   * Function to handle apply filter.
   * @param filter - Applied filter.
   */
  function onApplyFilter(filter: DashboardDataFilter): void {
    if (filter.name === DAHBOARD_DEFAULT_FILTER) {
      setDataFilter(null);
      setAppliedFilter({ key: Filters.dashboardDataFilter, data: null });
      return;
    }
    setDataFilter(filter);
    setAppliedFilter({ key: Filters.dashboardDataFilter, data: filter });
  }

  useEffect(() => {
    if (dataFilter?.type) {
      setExtendedLabel(dataFilter.name);
    } else {
      setExtendedLabel('All Devices');
    }
  }, [dataFilter]);

  useEffect(() => {
    if (!defaultDashboardFilter || hasAppliedDefaultFilter) {
      return;
    }

    if (
      dataFilter &&
      dataFilter.id === defaultDashboardFilter.id &&
      dataFilter.type === defaultDashboardFilter.type
    ) {
      setHasAppliedDefaultFilter(true);
      return;
    }

    setDataFilter(defaultDashboardFilter);
    setAppliedFilter({ key: Filters.dashboardDataFilter, data: defaultDashboardFilter });
    setHasAppliedDefaultFilter(true);
  }, [defaultDashboardFilter, dataFilter, hasAppliedDefaultFilter]);

  // eslint-disable-next-line jsdoc/require-jsdoc
  const hideLoaders = (): void => {
    setRefreshing(false);
    setLoading(false);
  };

  /**
   * Fetches getChartData api.
   * @param start - Start time.
   * @param end - End time.
   */
  const fetchData = useCallback(
    async (start: number, end: number): Promise<void> => {
      try {
        const payload: summaryAPIProps & { devices?: number[] } = {
          startDate: start,
          endDate: end,
          dataPointsDifference: clampResolution(resolution, start, end)
        };
        setLastSynced(new Date());
        if (selectedDevice) {
          payload.devices = [selectedDevice];
        } else if (dataFilter?.type === 'group') {
          const group = groupedDataRef.current.filter(
            (item: any) => item.groupId === dataFilter.id
          )[0];
          if (group) {
            payload.devices = group.sensorDetailsList.map((item: any) => item.serial_number);
          }
        }
        const [detialedChartDataResponse, minAndMaxDataResponse] = await Promise.all([
          getDeviceSummaryOverTime(payload),
          getMinAndMaxOfParameters(payload)
        ]);
        if (minAndMaxDataResponse.data?.length === 2) {
          const [min, max] = minAndMaxDataResponse.data;
          setMinMaxData({ min: min.parameters, max: max.parameters });
        }
        if (!detialedChartDataResponse?.data?.data) {
          graphDataRef.current = null;
          hideLoaders();
          setUpdateTrigger((prev) => !prev);
          return;
        }
        parametersRef.current.forEach((parameter) => {
          const parameterDataInResponse = detialedChartDataResponse?.data?.data?.find(
            (eachParameter: { data: number[][]; name: ParametersType }) =>
              eachParameter.name === parameter.parameterName
          );
          if (parameterDataInResponse) {
            const value = changeZeroValuesToNull(parameterDataInResponse.data);
            chartQueueDataRef.current[parameter.parameterName] = new Deque();
            chartQueueDataRef.current[parameter.parameterName].add(...value);
            if (timeSpan !== TimeSpan.Custom) {
              chartQueueDataRef.current[parameter.parameterName].prune(timePeriod);
            }
          }
        });
        hideLoaders();
        setUpdateTrigger((prev) => !prev);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        hideLoaders();
      }
    },
    [selectedDevice, dataFilter, timeSpan, timePeriod, resolution]
  );

  /**
   * Fetches getChartData api.
   * @param detialedChartDataResponse - Time span data.
   */
  const updateGraphData = useCallback(
    async (detialedChartDataResponse: summaryOverTimeResponse): Promise<void> => {
      try {
        const graphDataCurrent = chartQueueDataRef.current;
        if (graphDataCurrent) {
          if (detialedChartDataResponse.xAxis.length === 0) {
            return;
          }

          detialedChartDataResponse?.data?.forEach(
            (elt: { data: number[][]; name: ParametersType }) => {
              const value = changeZeroValuesToNull(elt.data);
              if (graphDataCurrent[elt.name]) {
                graphDataCurrent[elt.name] = manageQueue(
                  graphDataCurrent[elt.name],
                  value,
                  timePeriod
                );
              }
            }
          );
          setLoading(false);
          setUpdateTrigger((prev) => !prev);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    },
    [timePeriod]
  );

  useEffect(() => {
    if (parameters?.length === 0 && !chartQueueDataRef.current) {
      return;
    }
    if (!devicesLoading && timeSpanDate) fetchData(timeSpanDate?.startDate, timeSpanDate?.endDate);
  }, [timeSpanDate, devicesLoading, fetchData]);

  /**
   * Function to refresh the data.
   */
  const refreshData = useCallback(async (): Promise<void> => {
    const timeSpanData = handleTimeSpanChange(timeSpan, timeSpanDate);
    setRefreshing(true);
    setTimeSpanDate({ startDate: timeSpanData.startDate, endDate: timeSpanData.endDate });
  }, [timeSpan, timeSpanDate]);

  useEffect(() => {
    if (timeSpan === TimeSpan.RealTime) {
      const cleanup = setupEventSource({
        loading,
        timeSpan,
        selectedDevice,
        dataFilter,
        groupedData,
        dataSummary,
        setDataSummary,
        setGroupDataSummary,
        updateGraphData,
        cleanGraphData
      });
      return cleanup;
    }
    if (timeSpan === TimeSpan.Custom) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }
    const interval = setInterval(() => {
      refreshData();
    }, DEFAULT_POLLING_TIME);
    return () => clearInterval(interval);
  }, [selectedDevice, groupedData, dataFilter, loading, timeSpan, timeSpanDate, dataSummary]);

  return (
    <div className="flex flex-1 flex-col max-h-full py-4 ">
      <div>
        {timeSpanDate && (
          <DashboardFilters
            view={effectiveView}
            setView={setViewMode}
            availableViews={availableViews}
            onChangeTimeFilter={handleTimeChange}
            timeSpan={timeSpan}
            lastSynced={lastSynced}
            onRefresh={() => refreshData()}
            refreshing={refreshing}
            groupedData={groupedData}
            dataFilter={dataFilter}
            setDataFilter={(filter) => {
              onApplyFilter(filter);
            }}
            timeSpanDate={timeSpanDate}
            setTimeSpanDate={setTimeSpanDate}
            handleDownload={() => setDownloadDialogOpen(true)}
            handleIngest={() => setIngestDialogOpen(true)}
            resolution={clampResolution(
              resolution,
              timeSpanDate.startDate,
              timeSpanDate.endDate
            )}
            setResolution={setResolution}
          />
        )}
      </div>
      <div
        ref={containerRef}
        className="flex flex-1 flex-col box-border pt-4 max-h-full h-full gap-4"
      >
        {effectiveView === 'detailed' && (
          <DetailedPage
            minMaxData={minMaxData}
            setSidePanelParameter={setSidePanelParameter}
            dataFilter={dataFilter}
            extendedLabel={extendedLabel}
            groupedData={groupedData}
            setDataFilter={setDataFilter}
            timeSpan={timeSpan}
            timeSpanDate={timeSpanDate}
            loading={loading}
            chartQueueDataRef={chartQueueDataRef}
            setSelectedParameter={setSelectedParameter}
            devicesLoading={devicesLoading}
          />
        )}
        {effectiveView === 'map' && (
          <MapPage
            groups={groupedData}
            dataFilter={dataFilter}
            setDataFilter={setDataFilter}
            loading={devicesLoading}
            timeSpan={timeSpan}
            timeSpanDate={timeSpanDate}
          />
        )}
        {effectiveView === 'simple' && (
          <SimplifiedPage
            setDataFilter={setDataFilter}
            dataFilter={dataFilter}
            extendedLabel={extendedLabel}
            timeSpan={timeSpan}
            setSelectedParameter={setSelectedParameter}
            lineChartGraphData={chartQueueDataRef.current}
            loading={loading}
          />
        )}
      </div>
      {sidepanelParameter !== null && timeSpanDate && (
        <DashboardSideBar
          isModalOpen={sidepanelParameter !== null}
          handleClose={() => setSidePanelParameter(null)}
          data={sidepanelParameter}
          timeSpanDate={timeSpanDate}
          timeSpan={timeSpan}
          resolution={clampResolution(resolution, timeSpanDate.startDate, timeSpanDate.endDate)}
        />
      )}
      {downloadDialogOpen && (
        <DownloadModal
          open={downloadDialogOpen}
          setOpen={setDownloadDialogOpen}
          onDownload={() => {}}
        />
      )}
      {ingestDialogOpen && (
        <InjestDeviceDataModal open={ingestDialogOpen} setOpen={setIngestDialogOpen} />
      )}
    </div>
  );
}
